import { Component, EventEmitter, inject, Input, Output, AfterViewChecked, ElementRef } from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { MatCardModule } from '@angular/material/card'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatChipsModule } from '@angular/material/chips'
import { MatSelectModule } from '@angular/material/select'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatMenuModule } from '@angular/material/menu'
import { MatTooltipModule } from '@angular/material/tooltip'
import { MatDialog } from '@angular/material/dialog'
import { CdkDragHandle } from '@angular/cdk/drag-drop'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import {
  TemplateContentBlock,
  TemplateContentBlockType,
  TemplateContentBlockSource,
  CompletedTemplateContentBlockSource,
  TemplateContentBlockSourceType,
  FormattedTextBlock,
} from '../types/template.types'
import { AgendaItemSubCategory, CallResource, MinutesAnonymization, MinutesVerboseness } from '../../calls.service'
import { FormattedTextEditorComponent } from '../formatted-text-editor.component/formatted-text-editor.component'
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component'
import { PdfPreviewDialogComponent, PdfPreviewDialogData } from '../pdf-preview-dialog/pdf-preview-dialog.component'
import { ToastService } from '../../toast.service'
import { FileSizePipe, NgxFilesizeModule } from 'ngx-filesize'
import { truncateStringFromMiddle } from '../../utils'
import { LoggerService } from '../../logger.service'

export type FileUploadRequest = {
  blockId: number
  file: File
}
export const MAXIMUM_TOTAL_PAGES_FOR_CONTENT_BLOCK = 100

export const EMPTY_PROSEMIRROR_DOC = '{"type":"doc"}'

@Component({
  selector: 'app-template-item-content-block',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatMenuModule,
    MatTooltipModule,
    CdkDragHandle,
    TranslateModule,
    FormattedTextEditorComponent,
    NgxFilesizeModule,
  ],
  templateUrl: './template-item-content-block.component.html',
  styleUrls: ['./template-item-content-block.component.scss'],
  providers: [FileSizePipe],
})
export class TemplateItemContentBlockComponent implements AfterViewChecked {
  @Input({ required: true })
  block!: TemplateContentBlock
  @Input({ required: true })
  availableResources!: CallResource[]
  @Input()
  allowFileUpload = true
  @Input()
  shouldFocus = false
  @Input()
  superModeEnabled = false

  @Output()
  contentChanged = new EventEmitter<void>()
  @Output()
  blockRemoved = new EventEmitter<number>()
  @Output()
  fileUploadRequested = new EventEmitter<FileUploadRequest>()

  dialog = inject(MatDialog)
  translateService = inject(TranslateService)
  toastService = inject(ToastService)
  fileSizePipe = inject(FileSizePipe)
  elementRef = inject(ElementRef)
  loggerService = inject(LoggerService)

  TemplateContentBlockType = TemplateContentBlockType
  TemplateContentBlockSourceType = TemplateContentBlockSourceType
  MinutesVerboseness = MinutesVerboseness
  MinutesAnonymization = MinutesAnonymization
  AgendaItemSubCategory = AgendaItemSubCategory

  readonly maximumTotalSourceSizeForContentBlock = 30 * 1000 * 1000 // ~ 30 MB
  readonly maximumTotalPagesForContentBlock = MAXIMUM_TOTAL_PAGES_FOR_CONTENT_BLOCK

  ngAfterViewChecked(): void {
    if (this.shouldFocus) {
      const textarea = this.elementRef.nativeElement.querySelector(`#textarea-block-${this.block.id}`)
      if (textarea !== null) {
        setTimeout(() => {
          textarea.focus()
        }, 0)
      }
    }
  }

  markAsChanged() {
    this.contentChanged.emit()
  }

  removeContentBlock() {
    const blockTypeName = this.translateService.instant(`TEMPLATES.EDITOR.BLOCK_TYPES.${this.block.type}`)
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('TEMPLATES.EDITOR.REMOVE_CONTENT_BLOCK_DIALOG.TITLE'),
        description: this.translateService.instant('TEMPLATES.EDITOR.REMOVE_CONTENT_BLOCK_DIALOG.DESCRIPTION', { blockType: blockTypeName }),
        confirmLabel: this.translateService.instant('COMMON.DELETE'),
        cancelLabel: this.translateService.instant('COMMON.CANCEL'),
      },
    })

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.blockRemoved.emit(this.block.id)
      }
    })
  }

  get canRemoveTranscriptSource(): boolean {
    return (
      this.block.type !== TemplateContentBlockType.DISCUSSION_SUMMARY &&
      this.block.type !== TemplateContentBlockType.DISCUSSION_CHALLENGE &&
      this.block.type !== TemplateContentBlockType.BINARY
    )
  }

  addSource(sourceType: TemplateContentBlockSourceType, resource: CallResource | null) {
    if (sourceType === TemplateContentBlockSourceType.FILE_UPLOAD && resource !== null) {
      const currentSize = this.totalSizeOfSources
      const newSize = currentSize + resource.size
      if (newSize > this.maximumTotalSourceSizeForContentBlock) {
        this.toastService.showRaw(
          this.translateService.instant('TEMPLATES.EDITOR.CONTENT_BLOCK_SIZE_LIMIT_EXCEEDED', {
            maxSize: this.fileSizePipe.transform(this.maximumTotalSourceSizeForContentBlock),
          })
        )
        return
      }
      const currentPages = this.totalNumberOfPagesOfSources
      const resourcePages = resource.metadata?.numberOfPages ?? 0
      if (currentPages + resourcePages > MAXIMUM_TOTAL_PAGES_FOR_CONTENT_BLOCK) {
        this.toastService.showRaw(
          this.translateService.instant('TEMPLATES.EDITOR.CONTENT_BLOCK_PAGE_LIMIT_EXCEEDED', {
            maxPages: MAXIMUM_TOTAL_PAGES_FOR_CONTENT_BLOCK,
          })
        )
        return
      }
    }
    const newSource: CompletedTemplateContentBlockSource = {
      type: sourceType,
      templateContentBlockId: this.block.id,
      callResourceId: resource !== null ? resource.id : null,
      callResource: resource,
      isPending: false,
    }
    this.block.sources.push(newSource)
    this.markAsChanged()
  }

  removeSource(source: TemplateContentBlockSource) {
    if (source.isPending) {
      this.block.sources = this.block.sources.filter((s) => !(s.isPending && s.fileName === source.fileName))
    } else {
      this.block.sources = this.block.sources.filter((s) => s.type !== source.type || s.callResourceId !== source.callResourceId)
    }
    this.markAsChanged()
  }

  addFormattedTextBlock() {
    if (this.block.type !== TemplateContentBlockType.BINARY) {
      return
    }
    const maxIndex = this.block.data.formattedTextBlocks.length > 0 ? Math.max(...this.block.data.formattedTextBlocks.map((ftb) => ftb.index)) : -1
    this.block.data.formattedTextBlocks.push({
      content: EMPTY_PROSEMIRROR_DOC,
      index: maxIndex + 1,
    })
    this.markAsChanged()
  }

  removeFormattedTextBlock(textBlockIndex: number) {
    if (this.block.type !== TemplateContentBlockType.BINARY) {
      return
    }
    this.block.data.formattedTextBlocks = this.block.data.formattedTextBlocks
      .filter((formattedTextBlock) => formattedTextBlock.index !== textBlockIndex)
      .map((formattedTextBlock, formattedTextBlockIndex) => ({ ...formattedTextBlock, index: formattedTextBlockIndex }))
    this.markAsChanged()
  }

  changeFormattedTextBlockContent(textBlock: FormattedTextBlock, content: any) {
    textBlock.content = JSON.stringify(content)
    this.markAsChanged()
  }

  parseJsonStringToProseMirrorDoc(jsonString: string | null): any {
    if (jsonString === null) {
      return null
    }
    try {
      return JSON.parse(jsonString)
    } catch (error) {
      console.error('Failed to parse JSON string:', error)
      return null
    }
  }

  get showsStyleSettings(): boolean {
    return this.block.type !== TemplateContentBlockType.FIXED_TEXT && this.block.type !== TemplateContentBlockType.VARIABLE_TEXT
  }

  get showsSourceSettings(): boolean {
    return this.block.type !== TemplateContentBlockType.FIXED_TEXT
  }

  get allowSourceEditing(): boolean {
    return this.block.type !== TemplateContentBlockType.FIXED_TEXT && this.block.type !== TemplateContentBlockType.BINARY
  }

  get blockHasTranscriptSource(): boolean {
    return this.block.sources.some((source) => source.type === TemplateContentBlockSourceType.TRANSCRIPT)
  }

  get unusedAvailableResourcesForContentBlock(): CallResource[] {
    return this.availableResources.filter((resource) => !this.block.sources.some((s) => s.callResourceId === resource.id))
  }

  get nonOrphanSources(): TemplateContentBlockSource[] {
    return this.block.sources.filter((source) => {
      if (source.type === TemplateContentBlockSourceType.TRANSCRIPT) {
        return true
      }
      if (source.isPending === true) {
        return true
      }
      return source.callResourceId !== null && this.availableResources.some((r) => r.id === source.callResourceId)
    })
  }

  get totalSizeOfSources(): number {
    return this.block.sources.reduce((total, source) => {
      if (!source.isPending && source.type === TemplateContentBlockSourceType.FILE_UPLOAD && source.callResource !== null) {
        return total + source.callResource.size
      }
      return total
    }, 0)
  }

  get totalNumberOfPagesOfSources(): number {
    return this.block.sources.reduce((total, source) => {
      if (
        !source.isPending &&
        source.type === TemplateContentBlockSourceType.FILE_UPLOAD &&
        source.callResource !== null &&
        source.callResource.metadata !== null
      ) {
        return total + source.callResource.metadata.numberOfPages
      }
      return total
    }, 0)
  }

  get displayMissingSourceWarning(): boolean {
    return this.block.type !== TemplateContentBlockType.FIXED_TEXT && this.block.sources.length === 0
  }

  canAddResource(resource: CallResource): boolean {
    const currentSize = this.totalSizeOfSources
    const newSize = currentSize + resource.size
    if (newSize > this.maximumTotalSourceSizeForContentBlock) {
      return false
    }

    const currentPages = this.totalNumberOfPagesOfSources
    const resourcePages = resource.metadata?.numberOfPages ?? 0
    const newPages = currentPages + resourcePages
    if (newPages > MAXIMUM_TOTAL_PAGES_FOR_CONTENT_BLOCK) {
      return false
    }
    return true
  }

  truncateFileNameFromMiddle(fileName: string, maxLength: number = 20): string {
    return truncateStringFromMiddle(fileName, maxLength)
  }

  removeFileExtension(fileName: string): string {
    return fileName.replace(/\.[^/.]+$/, '')
  }

  onResourceMenuItemClick(event: MouseEvent, resource: CallResource): void {
    if (!this.canAddResource(resource)) {
      event.stopPropagation()
      return
    }
    this.addSource(TemplateContentBlockSourceType.FILE_UPLOAD, resource)
  }

  getResourceDisabledTooltip(resource: CallResource): string {
    const currentSize = this.totalSizeOfSources
    const newSize = currentSize + resource.size
    if (newSize > this.maximumTotalSourceSizeForContentBlock) {
      return this.translateService.instant('TEMPLATES.EDITOR.CONTENT_BLOCK_SIZE_LIMIT_EXCEEDED', {
        maxSize: this.fileSizePipe.transform(this.maximumTotalSourceSizeForContentBlock),
      })
    }

    const currentPages = this.totalNumberOfPagesOfSources
    const resourcePages = resource.metadata?.numberOfPages ?? 0
    const newPages = currentPages + resourcePages
    if (newPages > MAXIMUM_TOTAL_PAGES_FOR_CONTENT_BLOCK) {
      return this.translateService.instant('TEMPLATES.EDITOR.CONTENT_BLOCK_PAGE_LIMIT_EXCEEDED', {
        maxPages: MAXIMUM_TOTAL_PAGES_FOR_CONTENT_BLOCK,
      })
    }

    return ''
  }

  triggerFileUpload() {
    const fileInput = document.getElementById(`file-upload-${this.block.id}`)
    if (fileInput !== null) {
      fileInput.click()
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement
    if (input.files === null || input.files.length === 0) {
      return
    }
    const file = input.files[0]
    const currentSize = this.totalSizeOfSources
    const newSize = currentSize + file.size
    if (newSize > this.maximumTotalSourceSizeForContentBlock) {
      const formattedSize = this.fileSizePipe.transform(this.maximumTotalSourceSizeForContentBlock)
      this.toastService.showRaw(this.translateService.instant('TEMPLATES.EDITOR.CONTENT_BLOCK_SIZE_LIMIT_EXCEEDED', { maxSize: formattedSize }))
      input.value = ''
      return
    }
    this.fileUploadRequested.emit({ blockId: this.block.id, file })
    input.value = ''
  }

  openPdfPreview(source: TemplateContentBlockSource): void {
    if (source.isPending) {
      return
    }
    const resource = this.availableResources.find((resource) => resource.id === source.callResourceId) ?? null
    if (resource === null || resource.downloadUrl === null) {
      this.loggerService.error('Resource not found or download URL is missing. Cannot open PDF preview.')
      return
    }
    this.dialog.open(PdfPreviewDialogComponent, {
      width: '80vw',
      maxWidth: '1200px',
      height: '85vh',
      data: {
        downloadUrl: resource.downloadUrl,
        fileName: resource.fileName,
      } as PdfPreviewDialogData,
    })
  }
}
